import React, { useState } from 'react';
import { Smartphone, Monitor, Watch, Laptop, Gamepad2 } from 'lucide-react';

interface BrandLogoProps {
  id?: string | number;
  name?: string | null;
  logoUrl?: string;
  className?: string;
}

// Clean inline vector fallbacks if JSDelivr fails or is blocked
const renderFallbackSVG = (normalizedName: string) => {
  switch (normalizedName) {
    case 'apple':
    case 'ipad':
    case 'macbook':
    case 'apple watch':
      return (
        <svg viewBox="0 0 170 170" fill="currentColor" className="w-auto h-full max-h-[70%] object-contain">
          <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.34.13-9.13-1.92-14.35-6.12-3.53-2.88-7.4-7.61-11.61-14.19-7.14-11.05-12.28-23.71-15.41-37.98-3.12-14.26-2.61-26.47 1.52-36.6 4.13-10.11 10.16-16.14 18.06-18.06 4.58-1.03 9.4-1.12 14.47.24 5.06 1.35 8.71 2.45 10.96 3.3 2.24.84 4.54 1.28 6.89 1.3 2.6-.02 5.37-.53 8.35-1.53 2.98-1 6.53-2.24 10.65-3.71 6.27-2.15 12.33-2.94 18.2-2.35 13.9 1.23 24.31 6.87 31.25 16.92-12.44 7.54-18.45 17.75-18.03 30.64.44 10.05 4.19 18.22 11.24 24.49 7.05 6.27 15.35 9.61 24.89 10.02-2.41 7.21-5.75 14.26-10.02 21.12zM119.22 19.01c0 7.15-2.64 13.92-7.92 19.3-5.28 5.38-11.83 8.38-19.64 9.01.13-6.9 2.76-13.56 7.92-18.96 5.15-5.4 11.88-8.58 20.18-9.55-.54 7.2-.54 7.2-.54 7.2z"/>
        </svg>
      );
    case 'samsung':
      return (
        <span className="text-sm md:text-lg font-black uppercase tracking-[0.1em]">SAMSUNG</span>
      );
    case 'google':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-auto h-full max-h-[60%] object-contain">
          <path d="M12.24 10.285V14.4h6.887c-.648 2.41-2.519 4.113-5.136 4.113A5.5 5.5 0 1 1 19.5 12h4.5c0 6.627-5.373 12-12 12S0 18.627 0 12s5.373-12 12-12c3.425 0 6.526 1.44 8.74 3.76l-3.2 3.2c-1.428-1.341-3.37-2.16-5.54-2.16A5.5 5.5 0 0 0 6.74 12c0 3.038 2.462 5.5 5.5 5.5c2.519 0 4.212-1.694 4.542-4H12.24z" />
        </svg>
      );
    case 'xiaomi':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-auto h-full max-h-[60%] object-contain">
          <path fillRule="evenodd" clipRule="evenodd" d="M2 4C2 2.89543 2.89543 2 4 2H20C21.1046 2 22 2.89543 22 4V20C22 21.1046 21.1046 22 20 22H4C2.89543 22 2 21.1046 2 20V4ZM8.5 7.5c-1.38071 0-2.5 1.11929-2.5 2.5V16.5H8V11H9V16.5H11V10C11 8.61929 9.88071 7.5 8.5 7.5ZM13.5 16.5H15.5V11H16.5V16.5H18.5V10C18.5 8.61929 17.3807 7.5 16 7.5H13.5V16.5Z" />
        </svg>
      );
    case 'oppo':
      return (
        <svg viewBox="0 0 100 25" fill="currentColor" className="w-auto h-full max-h-[40%] object-contain">
          <path fillRule="evenodd" clipRule="evenodd" d="M14 6c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 13.5c-3.04 0-5.5-2.46-5.5-5.5s2.46-5.5 5.5-5.5 5.5 2.46 5.5 5.5-2.46 5.5-5.5 5.5zm25-13.5c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 13.5c-3.04 0-5.5-2.46-5.5-5.5s2.46-5.5 5.5-5.5 5.5 2.46 5.5 5.5-2.46 5.5-5.5 5.5zM64 6c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 13.5c-3.04 0-5.5-2.46-5.5-5.5s2.46-5.5 5.5-5.5 5.5 2.46 5.5 5.5-2.46 5.5-5.5 5.5zm25-13.5c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 13.5c-3.04 0-5.5-2.46-5.5-5.5s2.46-5.5 5.5-5.5 5.5 2.46 5.5 5.5-2.46 5.5-5.5 5.5z" />
        </svg>
      );
    case 'huawei':
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <span className="text-[11px] md:text-sm font-black uppercase tracking-[0.25em]">HUAWEI</span>
        </div>
      );
    case 'sony':
      return (
        <span className="text-sm md:text-lg font-bold uppercase tracking-[0.15em] font-serif">SONY</span>
      );
    case 'asus':
      return (
        <span className="text-[11px] md:text-sm font-black italic uppercase tracking-widest">ASUS</span>
      );
    case 'consoles':
      return (
        <div className="flex items-center gap-1.5 justify-center text-slate-600 group-hover:text-blue-600 transition-colors">
          <Gamepad2 className="w-5 h-5 md:w-7 md:h-7 stroke-[1.5]" />
          <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest">CONSOLES</span>
        </div>
      );
    default:
      return null;
  }
};

const renderIconFallback = (normalizedName: string) => {
  const iconProps = { className: "w-6 h-6 md:w-8 md:h-8 stroke-[1.5] text-slate-400 group-hover:text-blue-500 transition-colors" };
  if (normalizedName.includes('watch')) return <Watch {...iconProps} />;
  if (normalizedName.includes('macbook') || normalizedName.includes('laptop')) return <Laptop {...iconProps} />;
  if (normalizedName.includes('ipad') || normalizedName.includes('tablet')) return <Monitor {...iconProps} />;
  if (normalizedName.includes('console') || normalizedName.includes('playstation') || normalizedName.includes('xbox')) return <Gamepad2 {...iconProps} />;
  return <Smartphone {...iconProps} />;
};

export const BrandLogo: React.FC<BrandLogoProps> = ({ name, logoUrl, className = "" }) => {
  const [error, setError] = useState(false);

  const safeName = name || '';
  const cleanName = safeName.replace('SÉRIE ', '').trim();
  const normalizedName = cleanName.toLowerCase();
  
  // 1. Resolve source URL
  let resolvedUrl = logoUrl || '';
  if (resolvedUrl.startsWith('uploads/') || resolvedUrl.startsWith('./uploads/')) {
    resolvedUrl = '/' + resolvedUrl.replace(/^\.\//, '');
  }
  
  // To keep everything 100% exclusive to the domain and website without any external links,
  // we strictly use our premium local vector SVGs by default.
  // We only allow local uploads (starting with '/uploads/') or inline data.
  const isGovernedReference = resolvedUrl && (resolvedUrl.startsWith('/uploads/') || resolvedUrl.startsWith('/media/') || resolvedUrl.startsWith('/assets/') || resolvedUrl.startsWith('https://'));
  
  if (!isGovernedReference) {
    resolvedUrl = '';
  }

  const handleImgError = () => {
    setError(true);
  };

  // If we have an error or no URL, fall back to our premium native/inline items
  if (error || !resolvedUrl) {
    const hasColorClass = className.includes('text-');
    const defaultColorClasses = hasColorClass ? '' : 'text-black dark:text-white';

    // A. Visual Inline SVG falling back
    const svg = renderFallbackSVG(normalizedName);
    if (svg) {
      return (
        <div className={`flex items-center justify-center w-full h-full ${defaultColorClasses} ${className}`}>
          {svg}
        </div>
      );
    }

    // B. Icon fallbacks
    const icon = renderIconFallback(normalizedName);
    return (
      <div className="flex flex-col items-center justify-center gap-1 md:gap-2 text-center h-full w-full">
        {icon}
        <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-colors ${hasColorClass ? '' : 'text-black dark:text-white'} ${className}`}>
          {cleanName}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full w-full relative">
      <img
        src={resolvedUrl}
        alt={`Logo ${cleanName}`}
        title={cleanName}
        width={160}
        height={64}
        loading="lazy"
        decoding="async"
        onError={handleImgError}
        className={`max-h-full max-w-full object-contain ${className}`}
        referrerPolicy="no-referrer"
      />
    </div>
  );
};

export default BrandLogo;
